import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Form, Button } from 'react-bootstrap';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import bsCustomFileInput from 'bs-custom-file-input';
import PropdocsModal from '../../../../modal/PropdocsModal';
import { FolderTreeSelector } from '../../../../folder-tree-selector';
import { ReactComponent as CloseIcon } from '../../../../../shared/icons/close-icon.svg';
import { ReactComponent as DragIcon } from '../../../../../shared/icons/drag.svg';
import { FileUploadModalProps, FileData } from './types';
import { 
  MAX_UPLOAD_SIZE_IN_BYTES, 
  SupportedDocFileTypes, 
  SupportedTemplateFileTypes, 
  SupportedFileTypes,
  getSupportedExtensionsString 
} from './utils';
import { usePrevious } from '../../../../../utils/use-previous';
import { AddNewTemplateRestrictedEditing, AddNewTemplateDisableWordExport, ExtractDocumentTermsOnUpload } from '../../../../../consts/forms-constants';
import {
  ModalFormLayout,
  FormSection,
  FormField,
  ToggleField
} from '../../../form-components';

const FileUploadModal: React.FC<FileUploadModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  templateUpload = false,
  allowMultiple = false,
  folders = [],
  selectedFolderId = null,
  onFolderSelectionChange,
  foldersLoading = false,
  isClauseUpload = false,
  modalTitle = 'Upload File',
  acceptedFileTypes = '.pdf',
  showRestrictedEditing = false,
  showDisableWordExport = false,
  showExtractDocumentTerms = false,
}) => {
  const [selectedFile, setSelectedFile] = useState<FileData | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<FileData[]>([]);
  const [documentTitle, setDocumentTitle] = useState<string>('');
  const [isRestrictedEditingEnabled, setIsRestrictedEditingEnabled] = useState(false);
  const [isDisableWordExportEnabled, setIsDisableWordExportEnabled] = useState(false);
  const [extractDocumentTerms, setExtractDocumentTerms] = useState(false);
  const [errors, setErrors] = useState<{ title?: boolean; selectedFile?: string | boolean }>({});
  const isPreviousOpen = usePrevious(isOpen);
  
  const isDocxUpload = acceptedFileTypes.includes('docx');
  const filesForValidation = selectedFiles.length > 0 ? selectedFiles : (selectedFile ? [selectedFile] : []);
  const isValidExtension = filesForValidation.length > 0
    ? filesForValidation.every(file => {
        const supportedTypes = isDocxUpload 
          ? SupportedDocFileTypes 
          : templateUpload 
            ? SupportedTemplateFileTypes 
            : SupportedFileTypes;
        return Object.values(supportedTypes).includes(file.extension?.toLowerCase() || '');
      })
    : null;

  useEffect(() => {
    if (!isOpen) return;
    bsCustomFileInput.init('#file-upload-custom');
  }, [isOpen]);

  useEffect(() => {
    if (isPreviousOpen && !isOpen) {
      setSelectedFile(null);
      setSelectedFiles([]);
      setErrors({});
      setDocumentTitle('');
      setIsRestrictedEditingEnabled(false);
      setIsDisableWordExportEnabled(false);
      setExtractDocumentTerms(false);
    }
  }, [isOpen, isPreviousOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const hasFiles = selectedFiles.length > 0 || selectedFile;
    setErrors({
      title: !documentTitle.trim(),
      selectedFile: hasFiles 
        ? (isValidExtension ? '' : getSupportedExtensionsString(templateUpload, isDocxUpload)) 
        : true,
    });

    if (!hasFiles || !documentTitle || !isValidExtension) return;

    onConfirm({
      selectedFile: selectedFiles.length > 0 ? undefined : selectedFile || undefined,
      selectedFiles: selectedFiles.length > 0 ? selectedFiles : undefined,
      documentTitle,
      isRestrictedEditingEnabled,
      isDisableWordExportEnabled,
      selectedFolderId,
      extractDocumentTerms,
    });
  };

  const handleSelectedFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target || !e.target.files || e.target.files.length === 0) return;
    
    const files = Array.from(e.target.files);
    
    if (allowMultiple && !isDocxUpload) {
      // Handle multiple files
      const processedFiles: FileData[] = [];
      let filesProcessed = 0;
      
      files.forEach((file) => {
        if (file.size > MAX_UPLOAD_SIZE_IN_BYTES) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            selectedFile: `File ${file.name} exceeds ${MAX_UPLOAD_SIZE_IN_BYTES / (1024 * 1024)} MB`,
          }));
          return;
        }
        
        const fileNameParts = file.name.split('.');
        const fileExtension = fileNameParts[fileNameParts.length - 1];
        
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          processedFiles.push({
            fileName: file.name,
            fileData: reader.result,
            contentType: file.type,
            extension: fileExtension,
          });
          
          filesProcessed++;
          if (filesProcessed === files.length) {
            setSelectedFiles(prevFiles => [...prevFiles, ...processedFiles]);
            setSelectedFile(null);
            if (!documentTitle.trim() && selectedFiles.length === 0 && processedFiles.length > 0) {
              setDocumentTitle(processedFiles[0].fileName || '');
            }
          }
        };
        reader.onerror = () => {
          // File reading error handled by state
        };
      });
    } else {
      // Handle single file
      const file = files[0];
      
      if (file.size > MAX_UPLOAD_SIZE_IN_BYTES) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          selectedFile: `File size should not exceed ${MAX_UPLOAD_SIZE_IN_BYTES / (1024 * 1024)} MB`,
        }));
        return;
      }
      
      const fileNameParts = file.name.split('.');
      const fileExtension = fileNameParts[fileNameParts.length - 1];
      
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (!documentTitle.trim()) {
          setDocumentTitle(file.name);
        }
        
        setSelectedFile({
          fileName: file.name,
          fileData: reader.result,
          contentType: file.type,
          extension: fileExtension,
        });
        setSelectedFiles([]);
      };
      reader.onerror = (error) => {
        console.warn('File reading error: ', error);
      };
    }
    
    e.target.value = '';
  };

  // Drag and drop functionality
  const ItemType = 'file';

  const DraggableFileItem = ({ file, index, moveFile, removeFile }: any) => {
    const [{ isDragging }, drag] = useDrag({
      type: ItemType,
      item: { index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const [, drop] = useDrop({
      accept: ItemType,
      hover: (item: { index: number }) => {
        if (item.index !== index) {
          moveFile(item.index, index);
          item.index = index;
        }
      },
    });

    return (
      <div
        ref={(node) => drag(drop(node))}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px',
          marginBottom: '4px',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          opacity: isDragging ? 0.5 : 1,
          cursor: 'move',
        }}
      >
        <DragIcon style={{ width: '16px', height: '16px', marginRight: '8px', color: '#6c757d' }} />
        <span style={{ flex: 1, fontSize: '14px' }}>{file.fileName}</span>
        <button
          type="button"
          onClick={() => removeFile(index)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
          }}
        >
          <CloseIcon style={{ width: '16px', height: '16px', color: '#dc3545' }} />
        </button>
      </div>
    );
  };

  const moveFile = useCallback((fromIndex: number, toIndex: number) => {
    const updatedFiles = [...selectedFiles];
    const [movedFile] = updatedFiles.splice(fromIndex, 1);
    updatedFiles.splice(toIndex, 0, movedFile);
    setSelectedFiles(updatedFiles);
  }, [selectedFiles]);

  const removeFile = useCallback((index: number) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(updatedFiles);
    if (updatedFiles.length === 0) {
      setDocumentTitle('');
    }
  }, [selectedFiles]);

  return (
    <PropdocsModal
      size={'md'}
      show={isOpen}
      setShow={onClose}
      title={<FormattedMessage id="modal.upload-file" defaultMessage={modalTitle} />}
    >
      <ModalFormLayout onSubmit={handleSubmit}>
        <ModalFormLayout.Body>
          <FormSection>
            <FormField
              label={`${templateUpload ? 'Template' : 'Document'} Name`}
              required
            >
              <Form.Control
                type="text"
                onChange={(e: any) => setDocumentTitle(e.target.value)}
                value={documentTitle}
                isInvalid={errors.title}
              />
            </FormField>
          </FormSection>

          <FormSection>
            <Form.Group>
              <Form.File
                id="file-upload-custom"
                label={
                  selectedFiles.length > 0 
                    ? `${selectedFiles.length} file${selectedFiles.length === 1 ? '' : 's'} selected - Add More`
                    : allowMultiple && !isDocxUpload
                      ? "Select File(s)"
                      : "Select File"
                }
                custom
                accept={acceptedFileTypes}
                onChange={handleSelectedFileChange}
                isInvalid={!!errors.selectedFile}
                feedback={typeof errors.selectedFile === 'string' ? errors.selectedFile : null}
                multiple={allowMultiple}
              />
            </Form.Group>
          </FormSection>

          {/* Display selected files with drag and drop */}
          {selectedFiles.length > 0 && (
            <FormSection>
              <FormField label="Selected Files (drag to reorder):">
                <DndProvider backend={HTML5Backend}>
                  <div>
                    {selectedFiles.map((file, index) => (
                      <DraggableFileItem
                        key={`${file.fileName}-${index}`}
                        file={file}
                        index={index}
                        moveFile={moveFile}
                        removeFile={removeFile}
                      />
                    ))}
                  </div>
                </DndProvider>
              </FormField>
            </FormSection>
          )}

          {/* Restricted Editing option for DOCX template uploads */}
          {showRestrictedEditing && (
            <FormSection>
              <ToggleField
                messageId={AddNewTemplateRestrictedEditing.id}
                defaultMessage={AddNewTemplateRestrictedEditing.defaultMessage}
                tooltip={AddNewTemplateRestrictedEditing.TOOLTIP_MESSAGE}
                tooltipTestId="add-form-restricted-editing-tooltip"
                checked={isRestrictedEditingEnabled}
                onChange={setIsRestrictedEditingEnabled}
              />
            </FormSection>
          )}

          {/* Disable Word Export option */}
          {showDisableWordExport && (
            <FormSection>
              <ToggleField
                messageId={AddNewTemplateDisableWordExport.id}
                defaultMessage={AddNewTemplateDisableWordExport.defaultMessage}
                tooltip={AddNewTemplateDisableWordExport.TOOLTIP_MESSAGE}
                tooltipTestId="add-form-disable-word-export-tooltip"
                checked={isDisableWordExportEnabled}
                onChange={setIsDisableWordExportEnabled}
              />
            </FormSection>
          )}

          {/* Extract Document Terms option for PDF uploads */}
          {showExtractDocumentTerms && (
            <FormSection>
              <ToggleField
                messageId={ExtractDocumentTermsOnUpload.id}
                defaultMessage={ExtractDocumentTermsOnUpload.defaultMessage}
                tooltip={ExtractDocumentTermsOnUpload.TOOLTIP_MESSAGE}
                tooltipTestId="add-form-extract-terms-tooltip"
                checked={extractDocumentTerms}
                onChange={setExtractDocumentTerms}
              />
            </FormSection>
          )}

          {/* Folder selection for templates */}
          {templateUpload && onFolderSelectionChange && (
            <FormSection>
              <FormField
                label={isClauseUpload ? 'Clause Folder' : 'Template Folder'}
                helpText={
                  isClauseUpload
                    ? "Choose the folder where this clause will be uploaded. Select 'All Clauses (Root)' to upload it at the top level."
                    : "Choose the folder where this template will be uploaded. Select 'All Templates (Root)' to upload it at the top level."
                }
              >
                {foldersLoading ? (
                  <div className="text-muted">
                    <FormattedMessage id="message.loading-folders" defaultMessage="Loading folders..." />
                  </div>
                ) : (
                  <FolderTreeSelector
                    folders={folders}
                    selectedFolderId={selectedFolderId}
                    onSelectionChange={onFolderSelectionChange}
                    disabled={isLoading}
                    context={isClauseUpload ? 'clauses' : 'templates'}
                  />
                )}
              </FormField>
            </FormSection>
          )}
        </ModalFormLayout.Body>

        <ModalFormLayout.Footer>
          <Button variant="link" className="text-secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="secondary" type="submit" disabled={isLoading}>
            {isLoading && <span className="spinner-border spinner-border-sm me-2" />}
            {templateUpload ? 'Create Template' : isDocxUpload ? 'Import' : 'Upload'}
          </Button>
        </ModalFormLayout.Footer>
      </ModalFormLayout>
    </PropdocsModal>
  );
};

export default FileUploadModal;